// Dev-only "anonymous user who is out of tokens" simulation.
//
// This is the test rig for the generation gate / signup-login popup work: it
// lets the WHOLE running webapp be driven as an anonymous guest with N tokens
// (default 0), so every out-of-tokens surface — the sidebar Generate gate, the
// chat send gate, the low-credit warnings, PromptView's limit message — renders
// exactly as a real out-of-tokens guest would see it, without spending real
// tokens or juggling staging accounts.
//
// Seed rule B (DEV-only harnesses): every export below no-ops outside DEV and
// `readAnonZeroOverride()` returns null in any built bundle, so `import.meta.env.DEV`
// (statically false in prod/staging builds) dead-code-eliminates the whole path.
// AuthProvider reads the override each render and presents the auth context as
// "anonymous + zero tokens" when it's active.

export interface AnonZeroOverride {
  /** Token balance to report (free === total). 0 = out of tokens. */
  tokens: number;
}

const FLAG_KEY = 'tk_anon_zero';
const TOKENS_KEY = 'tk_anon_zero_tokens';

function devOnly(): boolean {
  return import.meta.env.DEV && typeof window !== 'undefined';
}

function clampTokens(n: number): number {
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
}

/**
 * One-time URL → localStorage sync, called once at app startup (main.tsx) so the
 * render path stays pure. `?anonZero=1[&tokens=N]` enables the sim and persists
 * it (survives client-side navigation + reloads); `?anonZero=0` (or `false`)
 * disables it. No-op outside DEV.
 */
export function syncAnonZeroFromUrl(): void {
  if (!devOnly()) return;
  const params = new URLSearchParams(window.location.search);
  if (!params.has('anonZero')) return;
  const v = params.get('anonZero');
  const on = v !== '0' && v !== 'false';
  if (on) {
    window.localStorage.setItem(FLAG_KEY, '1');
    if (params.has('tokens')) {
      window.localStorage.setItem(
        TOKENS_KEY,
        String(clampTokens(Number(params.get('tokens')))),
      );
    }
  } else {
    window.localStorage.removeItem(FLAG_KEY);
    window.localStorage.removeItem(TOKENS_KEY);
  }
}

/** The token balance the sim should report (0 when off / outside DEV). */
export function readAnonZeroTokens(): number {
  if (!devOnly()) return 0;
  return clampTokens(Number(window.localStorage.getItem(TOKENS_KEY) ?? '0'));
}

/**
 * The active override, or null when off / outside DEV. Pure read — call freely
 * from render (AuthProvider does, every render).
 */
export function readAnonZeroOverride(): AnonZeroOverride | null {
  if (!devOnly()) return null;
  if (window.localStorage.getItem(FLAG_KEY) !== '1') return null;
  return { tokens: readAnonZeroTokens() };
}

/**
 * Turn the sim on/off and set the token balance. Callers should reload (or
 * full-navigate) afterwards so AuthProvider re-resolves the session under the
 * new override. No-op outside DEV.
 */
export function setAnonZero(on: boolean, tokens = 0): void {
  if (!devOnly()) return;
  if (on) {
    window.localStorage.setItem(FLAG_KEY, '1');
    window.localStorage.setItem(TOKENS_KEY, String(clampTokens(tokens)));
  } else {
    window.localStorage.removeItem(FLAG_KEY);
    window.localStorage.removeItem(TOKENS_KEY);
  }
}
